/**
 * Tenant feature flags. Fetched once at boot from
 * `GET /feature-flags` (Moka-cached 60 s on the backend) and pinned
 * to the TanStack Query cache with a matching `staleTime`.
 *
 * UI calls `useFeatureFlag('key')`; route gating goes through
 * `<FeatureRoute flag="key" />`.
 */

import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { client } from '@/api/client';
import { qk } from '@/api/query-keys';

type FlagValue = boolean | string | number | null;
type FlagMap = Record<string, FlagValue>;

interface FeatureFlagRow {
  flag_key: string;
  is_enabled: boolean;
  value?: FlagValue;
}

const FeatureFlagContext = createContext<FlagMap>({});

const LAST_KEY = 'relay.flags';

/**
 * The flags from the last successful fetch in this browser. Used as the
 * query's initial (already stale) data, so the app renders at once on
 * the next load and refetches in the background — one round trip less
 * before the first screen asks for its data. A flag that turned off
 * takes effect when the refetch lands.
 */
export function lastKnownFlags(): FlagMap | undefined {
  try {
    const raw = window.localStorage.getItem(LAST_KEY);
    return raw ? (JSON.parse(raw) as FlagMap) : undefined;
  } catch {
    return undefined;
  }
}

// Exported for the v2 UiV2Gate, which mounts its own observer on the
// same query key to get truthful pending status (this provider's
// placeholderData hides it).
export async function fetchFlags(): Promise<FlagMap> {
  const { data, error } = await client.GET('/feature-flags' as never);
  if (error) {
    // Fail closed — backend matches this policy (Moka: cache miss ⇒
    // empty flag list until next fetch).
     
    console.warn('Feature flags unavailable; defaulting to OFF.');
    return {};
  }
  const rows = ((data as { flags?: FeatureFlagRow[] } | undefined)?.flags ?? []) as FeatureFlagRow[];
  const flags: FlagMap = Object.fromEntries(
    rows.map((r) => [
      r.flag_key,
      r.is_enabled ? (r.value ?? true) : false,
    ])
  );
  try {
    window.localStorage.setItem(LAST_KEY, JSON.stringify(flags));
  } catch {
    /* storage blocked: just no head start next time */
  }
  return flags;
}

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const { data } = useQuery({
    queryKey: qk.featureFlags.current(),
    queryFn: fetchFlags,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // Boot path: we want children to render eagerly; the hook below
    // returns `undefined` until flags arrive, so consumers should
    // treat `undefined` the same as "off or pending" per-flag. With
    // flags from a previous visit, start from those (stale → refetched).
    initialData: lastKnownFlags,
    initialDataUpdatedAt: 0,
    placeholderData: {},
  });
  const value = useMemo(() => data ?? {}, [data]);
  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlag<T = boolean>(key: string): T | undefined {
  const flags = useContext(FeatureFlagContext);
  return flags[key] as T | undefined;
}

export function FeatureRoute({
  flag,
  children,
}: {
  flag: string;
  children: ReactNode;
}) {
  const enabled = useFeatureFlag<boolean>(flag);
  const location = useLocation();
  if (enabled === false) {
    // 404 — gated routes should be indistinguishable from non-existent ones.
    return <Navigate to="/404" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}
